import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Info, Radio, Copy, Link2 } from "lucide-react";
import api, { API } from "@/lib/api";

export default function AdminOTA() {
  const [apartments, setApartments] = useState([]);
  const [syncing, setSyncing] = useState("");

  const load = () => api.get("/admin/apartments").then((r) => setApartments(r.data));
  useEffect(() => { load(); }, []);

  const sync = async (id) => {
    setSyncing(id);
    try {
      const r = await api.post(`/admin/apartments/${id}/sync-ical`);
      toast.success(`Sincronizzato: ${r.data.count} date bloccate`);
      load();
    } catch {
      toast.error("Errore sincronizzazione. Verifica gli URL iCal.");
    } finally {
      setSyncing("");
    }
  };

  const copyExport = (id) => {
    navigator.clipboard.writeText(`${API}/apartments/${id}/calendar.ics`);
    toast.success("URL iCal copiato — incollalo su Airbnb/Booking");
  };

  return (
    <div data-testid="admin-ota">
      <h1 className="font-serif font-light text-4xl text-petrol mb-2">Channel Manager / OTA Sync</h1>
      <p className="text-muted-foreground mb-8">Sincronizzazione bidirezionale dei calendari con Airbnb e Booking.com tramite iCal</p>

      <div className="bg-petrol text-white rounded-sm p-6 mb-8 flex gap-4" data-testid="ota-info">
        <Info size={22} className="text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
        <div className="text-sm text-white/85 space-y-2">
          <p><strong className="text-white">Come funziona la sincronizzazione bidirezionale:</strong></p>
          <p>• <strong className="text-white">Importazione (Airbnb/Booking → Sito):</strong> inserisci gli URL iCal di Airbnb e Booking nella scheda dell&rsquo;appartamento e premi &laquo;Sincronizza&raquo;. Le date già prenotate sulle OTA vengono bloccate sul sito.</p>
          <p>• <strong className="text-white">Esportazione (Sito → Airbnb/Booking):</strong> copia l&rsquo;URL iCal di esportazione qui sotto e incollalo nei calendari di Airbnb e Booking. Le prenotazioni dirette <em>confermate</em> sul sito bloccheranno automaticamente quelle date anche su Airbnb e Booking.</p>
          <p className="text-white/60 text-xs pt-1">Nota: Booking.com API richiede accesso come Connectivity Partner e Airbnb API il programma partner. In assenza delle API ufficiali, la sincronizzazione iCal è la prima versione funzionante.</p>
        </div>
      </div>

      <div className="space-y-5">
        {apartments.map((a) => (
          <div key={a.id} className="bg-card border border-line rounded-sm p-6" data-testid={`ota-apt-${a.id}`}>
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-sm overflow-hidden bg-beige shrink-0">
                  {a.photos?.[0] && <img src={a.photos[0]} alt="" className="w-full h-full object-cover" />}
                </div>
                <div>
                  <h3 className="font-serif text-xl text-petrol">{a.name}</h3>
                  <div className="flex gap-4 mt-1 text-xs">
                    <span className={`flex items-center gap-1 ${a.airbnb_ical ? "text-green-700" : "text-muted-foreground"}`}>
                      <Radio size={12} /> Airbnb iCal {a.airbnb_ical ? "✓" : "—"}
                    </span>
                    <span className={`flex items-center gap-1 ${a.booking_ical ? "text-green-700" : "text-muted-foreground"}`}>
                      <Radio size={12} /> Booking iCal {a.booking_ical ? "✓" : "—"}
                    </span>
                    <span className="text-muted-foreground">{a.blocked_dates?.length || 0} date importate</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => sync(a.id)} disabled={syncing === a.id || (!a.airbnb_ical && !a.booking_ical)} data-testid={`sync-${a.id}`}
                  className="flex items-center gap-2 bg-petrol text-white px-4 py-2.5 rounded-sm hover:bg-[#1A3A41] transition-colors text-sm disabled:opacity-40">
                  <RefreshCw size={15} className={syncing === a.id ? "animate-spin" : ""} /> Importa date OTA
                </button>
              </div>
            </div>

            {/* Export iCal feed */}
            <div className="mt-5 pt-5 border-t border-line">
              <p className="overline text-gold text-[10px] mb-2 flex items-center gap-1.5"><Link2 size={13} /> URL iCal di esportazione (incolla su Airbnb e Booking)</p>
              <div className="flex items-center gap-2 bg-cream border border-line rounded-sm px-3 py-2">
                <code className="text-xs text-petrol truncate flex-1" data-testid={`export-url-${a.id}`}>{`${API}/apartments/${a.id}/calendar.ics`}</code>
                <button onClick={() => copyExport(a.id)} data-testid={`copy-export-${a.id}`} className="text-gold hover:text-petrol transition-colors shrink-0">
                  <Copy size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
